import MuiTypography, { TypographyProps } from "@material-ui/core/Typography";
import { Variant } from "@material-ui/core/styles/createTypography";

import { isArray, isNumber, isString } from "lodash";
import { CSSProperties, ElementType, PropsWithChildren, useMemo } from "react";

import { AppColor } from "theme/app-color";
import typography from "theme/typography";
import { toCurrency } from "utils/helper";

const CustomVariant = {
  "Helper/12": {
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "14px",
  },
  "Body/14": {
    fontSize: "14px",
    fontWeight: 400,
    lineHeight: "22px",
  },
  "Body/16": {
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "26px",
  },
  "Header/16": {
    fontSize: "16px",
    fontWeight: 800,
    lineHeight: "32px",
  },
  "Header/20": {
    fontSize: "20px",
    fontWeight: 800,
    lineHeight: "32px",
  },
  "Header/24": {
    fontSize: "24px",
    fontWeight: 600,
    lineHeight: "32px",
  },
  "Header/32": {
    fontSize: "32px",
    fontWeight: 800,
    lineHeight: "52px",
  },
  "Header/48": {
    fontSize: "48px",
    fontWeight: 600,
    lineHeight: "24px",
  },
  "Subtitle/14": {
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: "22px",
  },
  "Subtitle/16": {
    fontSize: "16px",
    fontWeight: 700,
    lineHeight: "26px",
  },
  "Body/Bold": {
    fontSize: "16px",
    fontWeight: 600,
    lineHeight: "28px",
  },
  Body: {
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: "20px",
  },
  "Link/14": {
    fontSize: "14px",
    fontWeight: 500,
    lineHeight: "22px",
    textDecoration: "underline",
    cursor: "pointer",
  },
  "Caption/Bold": {
    fontSize: "12px",
    fontWeight: 700,
    lineHeight: "12px",
  },
};

const customVariantArr = Object.keys(CustomVariant).join(",");

export interface CustomTypographyProps
  extends Pick<
    TypographyProps,
    Exclude<keyof TypographyProps, "color" | "variant">
  > {
  isCurrency?: boolean;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  color?: keyof typeof AppColor | string;

  variant?: Variant | keyof typeof CustomVariant;
  component?: ElementType;
}

const Typography = (props: PropsWithChildren<CustomTypographyProps>) => {
  const {
    children,
    color,
    style,
    variant = "body1",
    isCurrency = false,
    minimumFractionDigits = 0,
    maximumFractionDigits = 0,
    ...rest
  } = props;
  const isHexa = color?.startsWith("#");
  const customColor = isHexa ? color : AppColor[color as keyof typeof AppColor];

  const customVariant =
    customVariantArr.includes(variant) &&
    CustomVariant[variant as keyof typeof CustomVariant];
  const variantStyle: CSSProperties | undefined =
    customVariantArr.includes(variant) && customVariant
      ? {
          fontSize: customVariant.fontSize,
          fontWeight: customVariant.fontWeight,
          lineHeight: customVariant.lineHeight,
          fontFamily: typography.fontFamily,
        }
      : undefined;

  let customRestProps = customColor
    ? { ...rest, style: { ...style, color: customColor } }
    : { ...rest, style };

  if (customVariant)
    customRestProps = {
      ...customRestProps,
      style: { ...customRestProps.style, ...variantStyle },
    };

  const customChildren = useMemo(() => {
    if (isCurrency && children) {
      if (isString(children)) {
        return children.replace(/\d+/g, (match) =>
          toCurrency(match, { minimumFractionDigits, maximumFractionDigits })
        );
      } else if (isNumber(children)) {
        return isNaN(children)
          ? 0
          : toCurrency(children, {
              minimumFractionDigits,
              maximumFractionDigits,
            });
      } else if (isArray(children)) {
        return children.map((item) => {
          if (isNumber(item)) {
            return toCurrency(item, {
              minimumFractionDigits,
              maximumFractionDigits,
            });
          }
          return item;
        });
      }
    }

    return children;
  }, [isCurrency, children, minimumFractionDigits, maximumFractionDigits]);

  return (
    <MuiTypography
      variant={!customVariant ? (variant as Variant) : "inherit"}
      {...customRestProps}
    >
      {customChildren}
    </MuiTypography>
  );
};

export default Typography;
